// Base URL for What-if Analysis backend (can be different from core API)
// Configure in your environment: REACT_APP_PROCESS_OPT_BASE_URL
const WHATIF_API_BASE = process.env.REACT_APP_PROCESS_OPT_BASE_URL ;

// Helper to get auth token
const getAuthToken = () => {
  try {
    const raw = sessionStorage.getItem("auth");
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.accessToken || null;
    }
  } catch (_) {}
  return null;
};

// Errors arrive FastAPI-style under `detail`, which may be a plain string, a
// validation array, or an object carrying a machine-readable code:
//   { "detail": { "error": "NO_TASKS", "message": "...", "process_id": 5 } }
// Reading only the top level here is what previously surfaced raw JSON to the UI.
const readApiError = (payload, status) => {
  const fallback = `Request failed (${status})`;
  const detail = payload?.detail ?? payload;

  if (typeof detail === "string" && detail.trim()) {
    return { code: null, message: detail };
  }

  if (Array.isArray(detail)) {
    const message = detail
      .map((item) => item?.msg || item?.message)
      .filter(Boolean)
      .join("; ");
    return { code: null, message: message || fallback };
  }

  if (detail && typeof detail === "object") {
    return {
      code: detail.error || detail.code || null,
      message: detail.message || detail.error || fallback,
    };
  }

  return { code: null, message: fallback };
};

// Custom fetch for what-if API (doesn't go through main api.js to avoid double prefix)
const whatIfFetch = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(endpoint, { ...options, headers });

  if (!response.ok) {
    let payload = null;
    try {
      const ct = response.headers.get("content-type") || "";
      if (ct.includes("application/json")) payload = await response.json();
    } catch (_) {}

    const { code, message } = readApiError(payload, response.status);
    const error = new Error(message);
    // Lets callers tailor the wording for known cases without string matching.
    error.code = code;
    error.status = response.status;
    throw error;
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return await response.json();
  }
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

// Fetch the What-if analysis for a process: cycle-time/cost metrics plus the
// as-is RACI staffing per task. Shape is normalized by utils/whatif/whatIfNormalize.
export async function getWhatIfAnalysis(processId) {
  if (!processId) throw new Error("processId is required");
  return whatIfFetch(`${WHATIF_API_BASE}/cms/whatif/${processId}`, {
    method: "GET",
  });
}

// Queuing analysis for a scenario (FBPM Ch.7 §7.2): the caller supplies the
// demand, each job's staffing and the service time per case its to-be assignments
// add up to; the endpoint returns utilization, weekly load and queue wait.
//
// It takes no process id — the request carries everything the arithmetic needs,
// so the endpoint is pure queueing math over whatever scenario is sent rather
// than a lookup against stored data.
//
// Request/response shapes are built and read by utils/whatif/whatIfQueueing.
export async function postQueuingAnalysis(body) {
  return whatIfFetch(`${WHATIF_API_BASE}/queuing-analysis`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
